import React, { useEffect, useMemo, useState } from 'react';
import {
  IonBadge,
  IonButton,
  IonContent,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonNote,
  IonPage,
  IonSearchbar,
  IonSegment,
  IonSegmentButton,
  IonSelect,
  IonSelectOption,
  IonSpinner,
  IonTextarea,
  useIonToast,
} from '@ionic/react';
import Header from 'src/components/Header';
import EntregaFolletoAPI, { ClienteFolleto } from 'src/services/entregaFolleto';
import { PAISES, PAIS_DEFAULT, Pais } from 'src/utils/countries';
import { onlyDigits, formatPyLocal, isValidPyPhone } from 'src/utils/phone';

type Props = {
  user: string;
  cargo?: string;
};

const EntregaFolleto: React.FC<Props> = () => {
  const [present] = useIonToast();

  const [clientes, setClientes] = useState<ClienteFolleto[]>([]);
  const [cargando, setCargando] = useState<boolean>(true);

  const [modo, setModo] = useState<'lista' | 'otro'>('lista');
  const [filtro, setFiltro] = useState<string>('');

  // Cliente elegido (de la lista) o nombre manual (buscador)
  const [seleccion, setSeleccion] = useState<ClienteFolleto | null>(null);
  const [clienteManual, setClienteManual] = useState<string>('');

  // Datos del contacto
  const [pais, setPais] = useState<Pais>(PAIS_DEFAULT);
  const [telefono, setTelefono] = useState<string>('');
  const [contactoNombre, setContactoNombre] = useState<string>('');
  const [observacion, setObservacion] = useState<string>('');

  const [enviando, setEnviando] = useState<boolean>(false);

  const cargarClientes = async () => {
    setCargando(true);
    try {
      const resp = await EntregaFolletoAPI.clientes();
      setClientes(resp.data?.datos || []);
    } catch (error: any) {
      const msg = error?.response?.data?.error || 'No se pudieron cargar los clientes';
      present({ message: msg, duration: 2500, color: 'danger' });
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargarClientes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clientesFiltrados = useMemo(() => {
    const q = filtro.trim().toLowerCase();
    if (!q) return clientes;
    return clientes.filter((c) => c.cliente_nombre.toLowerCase().includes(q));
  }, [clientes, filtro]);

  const esPy = pais.code === 'PY';

  const telefonoVisible = esPy ? formatPyLocal(telefono) : telefono;

  const onTelefonoChange = (valor: string) => {
    // Guardamos solo digitos; el formateo PY es solo visual.
    setTelefono(onlyDigits(valor));
  };

  const limpiarFormulario = () => {
    setSeleccion(null);
    setClienteManual('');
    setPais(PAIS_DEFAULT);
    setTelefono('');
    setContactoNombre('');
    setObservacion('');
  };

  const nombreClienteElegido = seleccion ? seleccion.cliente_nombre : clienteManual.trim();
  const hayClienteElegido = !!nombreClienteElegido;

  const telefonoValido = (): boolean => {
    const local = onlyDigits(telefono);
    if (!local) return false;
    if (esPy) return isValidPyPhone(pais.dial + local);
    return local.length >= 6; // validacion generica para otros paises
  };

  const registrar = async () => {
    if (!hayClienteElegido) {
      present({ message: 'Seleccione o ingrese un cliente', duration: 2200, color: 'warning' });
      return;
    }
    if (!telefonoValido()) {
      present({ message: 'Ingrese un numero de contacto valido', duration: 2200, color: 'warning' });
      return;
    }

    setEnviando(true);
    try {
      const payload: any = {
        contacto_pais: pais.dial,
        contacto_telefono: onlyDigits(telefono),
        contacto_nombre: contactoNombre.trim(),
        observacion: observacion.trim(),
      };
      if (seleccion && seleccion.entrega_id) {
        payload.entrega_id = seleccion.entrega_id;
      } else {
        payload.cliente_nombre = clienteManual.trim();
      }

      const resp = await EntregaFolletoAPI.registrar(payload);
      const enviado = resp.data?.mensaje_enviado;
      present({
        message: enviado
          ? 'Entrega registrada y WhatsApp enviado'
          : 'Entrega registrada (el WhatsApp se enviara cuando este disponible)',
        duration: 2800,
        color: 'success',
      });
      limpiarFormulario();
      setModo('lista');
      await cargarClientes();
    } catch (error: any) {
      const msg = error?.response?.data?.error || 'No se pudo registrar la entrega';
      present({ message: msg, duration: 2800, color: 'danger' });
    } finally {
      setEnviando(false);
    }
  };

  const renderEstado = (c: ClienteFolleto) => {
    if (c.estado === 'ENTREGADO') {
      return <IonBadge color="success">Entregado</IonBadge>;
    }
    return <IonBadge color="medium">Pendiente</IonBadge>;
  };

  return (
    <IonPage>
      <Header titulo="Reporte de Sostenibilidad" />
      <IonContent id="entrega-folleto" className="ion-padding">
        {!hayClienteElegido && (
          <>
            <IonSegment
              value={modo}
              onIonChange={(e) => setModo((e.detail.value as 'lista' | 'otro') || 'lista')}
            >
              <IonSegmentButton value="lista">
                <IonLabel>Mis clientes</IonLabel>
              </IonSegmentButton>
              <IonSegmentButton value="otro">
                <IonLabel>Otro cliente</IonLabel>
              </IonSegmentButton>
            </IonSegment>

            {modo === 'lista' && (
              <>
                <IonSearchbar
                  value={filtro}
                  onIonChange={(e) => setFiltro(e.detail.value || '')}
                  placeholder="Buscar cliente"
                  debounce={150}
                />
                {cargando ? (
                  <div className="ion-text-center ion-padding">
                    <IonSpinner />
                  </div>
                ) : (
                  <IonList>
                    {clientesFiltrados.length === 0 && (
                      <IonItem>
                        <IonLabel className="ion-text-wrap">
                          No se encontraron clientes.
                        </IonLabel>
                      </IonItem>
                    )}
                    {clientesFiltrados.map((c) => (
                      <IonItem
                        button
                        key={c.entrega_id}
                        onClick={() => setSeleccion(c)}
                      >
                        <IonLabel className="ion-text-wrap">{c.cliente_nombre}</IonLabel>
                        {renderEstado(c)}
                      </IonItem>
                    ))}
                  </IonList>
                )}
              </>
            )}

            {modo === 'otro' && (
              <>
                <IonItem>
                  <IonLabel position="stacked">Nombre del cliente</IonLabel>
                  <IonInput
                    value={clienteManual}
                    placeholder="Ingrese el nombre del cliente"
                    onIonChange={(e) => setClienteManual(e.detail.value || '')}
                  />
                </IonItem>
                <IonButton
                  expand="block"
                  className="ion-margin-top"
                  disabled={!clienteManual.trim()}
                  onClick={() => { /* continua al formulario de contacto */ }}
                >
                  Continuar
                </IonButton>
                <IonNote className="ion-padding-start">
                  Use esta opcion si el cliente no esta en su lista.
                </IonNote>
              </>
            )}
          </>
        )}

        {hayClienteElegido && (
          <>
            <IonItem lines="none">
              <IonLabel className="ion-text-wrap">
                <h2>{nombreClienteElegido}</h2>
                <IonNote>Entrega del Reporte de Sostenibilidad</IonNote>
              </IonLabel>
              <IonButton fill="clear" slot="end" onClick={limpiarFormulario}>
                Cambiar
              </IonButton>
            </IonItem>

            <IonItem>
              <IonLabel position="stacked">Pais</IonLabel>
              <IonSelect
                value={pais.dial}
                onIonChange={(e) => {
                  const sel = PAISES.find((p) => p.dial === e.detail.value);
                  if (sel) setPais(sel);
                }}
                interface="action-sheet"
              >
                {PAISES.map((p) => (
                  <IonSelectOption key={p.code} value={p.dial}>
                    {p.nombre} ({p.dial})
                  </IonSelectOption>
                ))}
              </IonSelect>
            </IonItem>

            <IonItem>
              <IonLabel position="stacked">Telefono de contacto</IonLabel>
              <IonInput
                type="tel"
                inputmode="tel"
                value={telefonoVisible}
                placeholder={esPy ? '9XX XXX XXX' : 'Numero de contacto'}
                onIonChange={(e) => onTelefonoChange(e.detail.value || '')}
              />
            </IonItem>

            <IonItem>
              <IonLabel position="stacked">Nombre del contacto (opcional)</IonLabel>
              <IonInput
                value={contactoNombre}
                placeholder="A quien se le entrego"
                onIonChange={(e) => setContactoNombre(e.detail.value || '')}
              />
            </IonItem>

            <IonItem>
              <IonLabel position="stacked">Observacion (opcional)</IonLabel>
              <IonTextarea
                value={observacion}
                autoGrow
                onIonChange={(e) => setObservacion(e.detail.value || '')}
              />
            </IonItem>

            <IonButton
              expand="block"
              className="ion-margin-top"
              disabled={enviando}
              onClick={registrar}
            >
              {enviando ? <IonSpinner name="dots" /> : 'Registrar entrega'}
            </IonButton>
          </>
        )}
      </IonContent>
    </IonPage>
  );
};

export default EntregaFolleto;
